import { forwardRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Avatar,
  Box,
  Button,
  Divider,
  Menu,
  Typography,
  usePopover,
  useScreenSize,
} from '@robocorp/components';
import {
  IconCode,
  IconDownloadCloud,
  IconHelp,
  IconLightBulb,
  IconPageFlip,
  IconPuzzle,
} from '@robocorp/icons/iconic';
import { IconLogoSlack } from '@robocorp/icons/logos';
import { styled } from '@robocorp/theme';
import remark from '~/assets/onboarding/welcome/remark.svg';
import { DocsLinks, ExternalLinks } from '~/constants';
import { useActions, useSelector } from '~/hooks';

const RemarkLink = styled(Menu.Link)`
  align-items: flex-start;
  padding-top: ${({ theme }) => theme.space.$8};
  padding-bottom: ${({ theme }) => theme.space.$8};
  height: auto;

  > div,
  p {
    overflow: visible;
  }
`;

const HelpMenuBase = forwardRef<HTMLDivElement, Record<string, unknown>>((props, forwardedRef) => {
  const { t } = useTranslation();
  const showButtonLabel = !useScreenSize('m');

  const { toggleSlackDialog } = useActions(({ uiActions }) => ({
    toggleSlackDialog: uiActions.toggleSlackDialog,
    toggleFeedbackDialog: uiActions.toggleFeedbackDialog,
    toggleServiceDeskDialog: uiActions.toggleServiceDeskDialog,
  }));

  return (
    <Box ref={forwardedRef} {...props}>
      <Menu
        trigger={
          <Button icon={IconHelp} variant="ghost">
            {showButtonLabel ? t('help') : undefined}
          </Button>
        }
        placement="bottom"
      >
        <Menu.Link icon={IconCode} href={DocsLinks.LINK_PYTHON_QUICKSTART} target="_blank">
          {t('helpMenu.quickStart')}
        </Menu.Link>

        <Menu.Link icon={IconPageFlip} href={DocsLinks.PRODUCT_MANUAL} target="_blank">
          {t('helpMenu.documentation')}
        </Menu.Link>

        <Menu.Link icon={IconPuzzle} href={ExternalLinks.ROBOCORP_PORTAL} target="_blank">
          {t('helpMenu.portal')}
        </Menu.Link>

        <Menu.Link
          icon={IconLightBulb}
          href={DocsLinks.CHANGELOG}
          target="_blank"
          className="rn-badge"
        >
          {t('helpMenu.releaseNotes')}
        </Menu.Link>

        <Menu.Link icon={IconDownloadCloud} href={ExternalLinks.DOWNLOADS} target="_blank">
          {t('helpMenu.downloads')}
        </Menu.Link>

        <Divider />

        <RemarkLink
          icon={<Avatar src={remark} size="small" alt="remark" />}
          href={ExternalLinks.LINK_REMARK}
          target="_blank"
          description={t('helpMenu.remarkDescription')}
        >
          {t('helpMenu.remark')}
        </RemarkLink>

        <Divider />

        <Menu.Link icon={IconLogoSlack} onClick={toggleSlackDialog}>
          {t('helpMenu.joinSlack')}
        </Menu.Link>

        <Divider />
      </Menu>
    </Box>
  );
});

export const HelpMenu = () => {
  const { t } = useTranslation();

  const [helpTooltipOpen, setHelpTooltipOpen] = useState(false);
  const { referenceRef, referenceProps, PopoverContent } = usePopover({
    open: helpTooltipOpen,
    placement: 'top',
  });

  const dismissingGettingStarted = useSelector(
    (state) => state.entityStates.users.current.dismissingGettingStarted,
  );

  useEffect(() => {
    if (dismissingGettingStarted) {
      setHelpTooltipOpen(true);
    }
  }, [dismissingGettingStarted]);

  return (
    <>
      <PopoverContent>
        <Box
          display="flex"
          flexDirection="column"
          flex={1}
          p="$16"
          borderRadius="$8"
          boxShadow="medium"
          backgroundColor="background.primary"
        >
          <Typography fontSize="$12" mb={8}>
            {t('onboarding.helpMenuInfo')}
          </Typography>
          <span>
            <Button size="small" variant="secondary" onClick={() => setHelpTooltipOpen(false)}>
              {t('hide')}
            </Button>
          </span>
        </Box>
      </PopoverContent>
      <HelpMenuBase ref={referenceRef} {...referenceProps} />
    </>
  );
};
